import { STORAGE } from "./constants";
import type { DocDefinition, EditorSession, SessionState } from "./types";

/** 编辑器纯逻辑：不碰 DOM，供 editor.ts 使用并可被 vitest 直测。 */

export const DRAFT_DEBOUNCE_MS = 800;
export const WORD_COUNT_DEBOUNCE_MS = 300;
export const NOTE_MAX_LENGTH = 120;

export function canEditDoc(doc: DocDefinition | undefined, session: SessionState): boolean {
  return !!doc && doc.group === "draft" && session.isAdmin;
}

/** 前端文档地址 /content/chapters/… → R2 内容路径 chapters/… */
export function contentPathOf(doc: DocDefinition): string {
  return doc.file.replace(/^\/content\//, "");
}

/** 与后端 normalizeMarkdown 同构：去 BOM、CRLF/孤 CR → LF、恰好一个结尾换行。 */
export function normalizeContent(text: string): string {
  let value = String(text ?? "");
  if (value.charCodeAt(0) === 0xfeff) value = value.slice(1);
  value = value.replace(/\r\n?/g, "\n");
  return value.replace(/\n*$/, "\n");
}

/** 镜像后端 400 规则的客户端预校验；通过返回 null。 */
export function validateChapter(content: string): string | null {
  if (!content.trim()) return "正文不能为空";
  if (!content.trimStart().startsWith("#")) return "正文必须以「# 章节标题」开头，请保留首行标题";
  return null;
}

/**
 * 源稿 → 编辑器分块：按空行切自然段，段内单个换行保留。
 * 三个以上连续换行会折叠为一个段边界（与 Markdown 渲染语义一致）。
 */
export function splitBlocks(source: string): string[] {
  return normalizeContent(source)
    .split(/\n{2,}/)
    .map((block) => block.replace(/^\n+/, "").replace(/\n+$/, ""))
    .filter((block) => block.trim().length > 0);
}

/** 编辑器分块 → 源稿：空块丢弃，块间恰好一个空行，结尾一个换行。 */
export function joinBlocks(blocks: string[]): string {
  const kept = blocks
    .map((block) => block.replace(/\n+$/, "").replace(/^\n+/, ""))
    .filter((block) => block.trim().length > 0);
  return kept.length ? `${kept.join("\n\n")}\n` : "";
}

export interface EditorDraft {
  content: string;
  baseRelease: string;
  savedAt: number;
}

export function draftKey(docId: string): string {
  return STORAGE.editDraft + docId;
}

export function serializeDraft(draft: EditorDraft): string {
  return JSON.stringify(draft);
}

export function parseDraft(raw: string | null): EditorDraft | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<EditorDraft> | null;
    if (!value || typeof value !== "object") return null;
    if (typeof value.content !== "string" || !value.content) return null;
    if (typeof value.baseRelease !== "string") return null;
    return {
      content: value.content,
      baseRelease: value.baseRelease,
      savedAt: typeof value.savedAt === "number" ? value.savedAt : 0,
    };
  } catch {
    return null;
  }
}

const numberFormatter = new Intl.NumberFormat("zh-CN");

/** 历史列表的字数变化标注；older 为 null 表示这是最早的版本。 */
export function formatCharsDelta(current: number, older: number | null): string {
  if (older === null) return `首版 · ${numberFormatter.format(current)} 字`;
  const delta = current - older;
  if (delta > 0) return `+${numberFormatter.format(delta)} 字`;
  if (delta < 0) return `-${numberFormatter.format(-delta)} 字`;
  return "±0 字";
}

export function buildRestoreNote(revisionId: number): string {
  return `恢复至 版本#${revisionId}`;
}

/** 离开编辑态是否需要确认：有编辑会话且当前内容与最近已知服务端内容不同。 */
export function needsLeaveConfirm(editor: EditorSession | null, currentContent: string): boolean {
  return editor !== null && currentContent !== editor.savedContent;
}
